import { Calendar as CalendarIcon, CircleX } from "lucide-react";
import { DateTime } from "luxon";
import * as React from "react";

import { cn } from "@/lib/utils";
import { useMask } from "@react-input/mask";
import { SelectSingleEventHandler } from "react-day-picker";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";

interface DateTimePickerProps {
    date?: DateTime;
    setDate?: (date?: DateTime) => void;
    placeholder?: string;
    className?: string;
    allowClear?: boolean;
    disabled?: boolean;
}

export function DateTimePicker({ date, setDate, placeholder = "Select a date", className, allowClear, disabled }: DateTimePickerProps) {
    const [open, setOpen] = React.useState(false);
    const [selectedDateTime, setSelectedDateTime] = React.useState<DateTime | undefined>(date);
    const [month, setMonth] = React.useState<Date | undefined>(date?.toJSDate());
    const [inputValue, setInputValue] = React.useState(selectedDateTime?.toFormat("dd/MM/yyyy HH:mm"));
    const inputRef = useMask({ mask: "__/__/____ __:__", replacement: { _: /\d/ }, showMask: true });

    React.useEffect(() => {
        setSelectedDateTime(date);
        setInputValue(date?.toFormat("dd/MM/yyyy HH:mm"));
        setMonth(date?.toJSDate());
    }, [date]);

    const handleSelect: SelectSingleEventHandler = (_day, selected) => {
        const selectedDay = DateTime.fromJSDate(selected);
        const modifiedDay = selectedDay.set({
            hour: selectedDateTime?.hour,
            minute: selectedDateTime?.minute
        });

        setSelectedDateTime(modifiedDay);
        setInputValue(modifiedDay.toFormat("dd/MM/yyyy HH:mm"));
        if (setDate) {
            setDate(modifiedDay);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <div>
                    {!open ? (
                        <div className={cn("flex", className)}>
                            <Button
                                type="button"
                                variant="outline"
                                className={cn("min-w-0 flex-1 items-center justify-start text-left font-normal", !selectedDateTime && "text-muted-foreground")}
                                onClick={() => setOpen(true)}
                                disabled={disabled}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                                <span className="truncate">{selectedDateTime ? selectedDateTime.toFormat("DDD HH:mm") : placeholder}</span>
                            </Button>
                            {allowClear && selectedDateTime && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="ml-1 shrink-0"
                                    aria-label="Clear date"
                                    onClick={() => {
                                        setSelectedDateTime(undefined);
                                        setDate?.(undefined);
                                    }}
                                    disabled={disabled}
                                >
                                    <CircleX className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Input
                            ref={inputRef}
                            value={inputValue}
                            onChange={e => {
                                setInputValue(e.target.value);
                                try {
                                    const [day, time] = e.target.value.split(" ");
                                    const modifiedDay = DateTime.fromFormat(day, "dd/MM/yyyy", { locale: "fr-FR" });
                                    const modifiedTime = DateTime.fromFormat(time, "HH:mm", { locale: "fr-FR" });
                                    const newDate = modifiedDay.set({ hour: modifiedTime.hour, minute: modifiedTime.minute });
                                    if (newDate.isValid) {
                                        setSelectedDateTime(newDate);
                                        setMonth(modifiedDay.toJSDate());
                                        setDate?.(newDate);
                                    } else {
                                        setSelectedDateTime(undefined);
                                        setDate?.(undefined);
                                    }
                                } catch (error) {
                                    console.error("error", error);
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    setOpen(false);
                                }
                            }}
                            autoFocus
                        />
                    )}
                </div>
            </PopoverAnchor>
            <PopoverContent
                className="w-auto p-0"
                onOpenAutoFocus={e => e.preventDefault()}
                onInteractOutside={e => {
                    if (e.target === inputRef.current) e.preventDefault();
                }}
            >
                <div className="flex items-start gap-4 p-2">
                    <Calendar mode="single" selected={selectedDateTime?.toJSDate()} onSelect={handleSelect} month={month} onMonthChange={setMonth} />
                    <div className="flex flex-col gap-2 pr-2 pt-2">
                        <Input
                            type="time"
                            step="60"
                            value={selectedDateTime ? selectedDateTime.toFormat("HH:mm") : ""}
                            onChange={e => {
                                const [hourStr, minuteStr] = e.target.value.split(":");
                                const parsedHour = Number(hourStr ?? "0");
                                const parsedMinute = Number(minuteStr ?? "0");
                                const baseDate = selectedDateTime ?? DateTime.now();
                                const newDate = baseDate.set({ hour: parsedHour, minute: parsedMinute });
                                setSelectedDateTime(newDate);
                                setInputValue(newDate.toFormat("dd/MM/yyyy HH:mm"));
                                setDate?.(newDate);
                            }}
                            className="bg-background"
                        />
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
